import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faPlus } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createAccountType } from "../../api/Agreement";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import EditForm from "../../components/EditForm";
import useAssignableLicenseOptions from "../../hooks/useAssignableLicenseOptions";

const CreateAccountTypePage = () => {
  const { getAccessToken, user } = useAuth();
  const { agreementPublicId } = useParams();
  const navigate = useNavigate();
  const licenseOptions = useAssignableLicenseOptions(agreementPublicId);
  const [hasChanges, setHasChanges] = useState(false);
  const [accountType, setAccountType] = useState({
    agreementPublicId: agreementPublicId,
    name: "",
    price: 0,
    status: "",
    comment: "",
    // PBI #299: seeded so a create without a typed description posts "" rather than
    // an absent property.
    customerDescription: "",
    licenseMpn: "",
    isDefault: false,
    // PBI #391: default is an Entra-only account type.
    isOnPremAccount: false,
  });

  const handleBackClick = () => {
    navigate(-1);
  };

const handleCreateAccountType = async () => {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      toast.error("Authorization failed. Please log in again.");
      return;
    }

    // Kontrollera att alla fält är ifyllda
    if (!accountType.agreementPublicId) {
      toast.error("The accountType must be associated with an agreement.");
      return;
    }

    await createAccountType(accountType, accessToken);

    toast.success("accountType created successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
  
} catch (error) {
    console.error("Failed to create accountType:", error);
    toast.error("Failed to create accountType");
  }
  navigate(-1);
};

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Create account type</h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary" onClick={handleBackClick}>
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={!hasChanges} onClick={handleCreateAccountType}>
                  Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={accountType}
                setData={setAccountType}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  { key: "accountTypePublicId", label: "Account type ID", type: "text", disabled: true}, 
                  { key: "name", label: "Name", type: "text" },
                  { key: "price", label: "Price", type: "number"},
                  {
                    key: "status",
                    label: "Status",
                    type: "dropdown",
                    options: [
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ],
                  },
                  { key: "comment", label: "Comment (internal)", type: "text" },
                  // PBI #299: shown to the customer under the account type name in the
                  // customer portal. Capped to the server's [StringLength(1000)].
                  {
                    key: "customerDescription",
                    label: "Description (shown to the customer)",
                    type: "text-area",
                    maxLength: 1000,
                  },
                  // PBI #318: the licence this account type implies. Empty means an
                  // account type change escalates to Diflex instead of swapping.
                  {
                    key: "licenseMpn",
                    label: "Licence part number (MPN)",
                    type: "suggest",
                    options: licenseOptions,
                  },
                  { key: "minAmount", label: "Minimum amount", type: "number"},
                  { key: "createdBy", label: "Created by", type: "text", disabled: true },
                  { key: "createdDateTimeUTC", label: "Created date", type: "date", disabled: true },
                  { key: "updatedBy", label: "Updated by", type: "text", disabled: true },
                  { key: "updatedDateTimeUTC", label: "Updated date", type: "date", disabled: true },
                  { key: "isDefault", label: "Is default", type: "switch" },
                  {
                    key: "isOnPremAccount",
                    label: "Account lives in on-prem AD (Diflex creates it by hand, no Entra account is created)",
                    type: "switch",
                  },
                ]}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateAccountTypePage;